import {
    DistanceSample,
    sampleDoseAlongLine,
    sampleImage3DAlongLine,
} from '~/domain/intensity/lineProfileSampling';
import {
    SummaryStatistics,
    summaryStatistics,
} from '~/domain/statistics/summaryStatistics';
import { Image3D } from '~/domain/patient/image3d';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { DoseUnit } from '~/domain/dicom/dicomDose';
import {
    doseUnitLabel,
    imageIntensityUnit,
    toDisplayIntensity,
} from '~/domain/dicom/intensityUnits';
import { LineProfile } from '~/domain/intensity/lineProfile';

/**
 * One dataset sampled along a line profile. Datasets sharing a `unit` are
 * overlaid on one chart; different units are never mixed on one axis.
 */
export interface ProfileDataset {
    /** Stable key — series UID for an image, SOP instance UID for a dose. */
    readonly key: string;
    /** Series description; may contain PHI → render via SensitiveInfoTypography. */
    readonly label: string;
    /** DICOM modality, e.g. `"CT"`, `"MR"`, `"RTDOSE"`. */
    readonly modality: string;
    /** Display unit, e.g. `"HU"`, `"Gy"`, `""` (unitless). */
    readonly unit: string;
    /** Curve colour, stable across the profile's datasets. */
    readonly color: string;
    /** True for the image the line was actually drawn on. */
    readonly isOwn: boolean;
    /** Samples in native units, ordered by increasing distance from the start. */
    readonly samples: DistanceSample[];
    /** Min/max/mean/SD of the samples, in native units. */
    readonly stats: SummaryStatistics;
    /**
     * Native → display value. MUST be linear through the origin: the SD is
     * scaled through it, so an offset would silently corrupt it.
     */
    readonly toDisplay: (value: number) => number;
}

/** Datasets that share one display unit, overlaid on a single chart. */
export interface ProfileUnitGroup {
    readonly unit: string;
    readonly datasets: ProfileDataset[];
}

/**
 * How many loaded datasets the comparison leaves out, by reason. Charts that
 * drop a dataset without saying so look complete, and the user has no way to
 * tell that they are not.
 */
export interface ProfileOmissions {
    /**
     * Datasets that are not in the profile's frame of reference, or whose frame
     * is unknown. Comparing them would need a registration transform, which
     * this comparison never applies.
     */
    readonly otherFrameOfReference: number;
    /**
     * Datasets that could not be sampled: their values could not be read, or
     * the line misses their grid or only clips it.
     */
    readonly unsampleable: number;
}

/** One profile's comparison charts, and what they leave out. */
export interface ProfileComparison {
    readonly unitGroups: ProfileUnitGroup[];
    readonly omitted: ProfileOmissions;
}

/** Curve palette; index 0 (blue) is reserved for the profile's own image. */
const DATASET_COLORS = [
    '#4FC3F7',
    '#FFB74D',
    '#81C784',
    '#BA68C8',
    '#E57373',
    '#FFD54F',
    '#4DB6AC',
];

/** Curve colour for the dataset at `index`; cycles past 7 curves. */
function datasetColor(index: number): string {
    return DATASET_COLORS[index % DATASET_COLORS.length];
}

/** A dataset before a colour is assigned; `failure` feeds the omission warning. */
type DatasetSeed = Omit<ProfileDataset, 'color' | 'stats'> & {
    readonly failure: string | null;
};

/**
 * Sample, or degrade to no samples naming why. The lazy pixel getters can throw
 * (e.g. an unbuildable PET volume), and `resampleAtSpacing` throws on invalid
 * line geometry — a caller defect that placement and drag rules already
 * prevent. The caller warns and omits; see {@link buildProfileComparison}.
 */
function sampleOrOmit(sample: () => DistanceSample[]): {
    samples: DistanceSample[];
    failure: string | null;
} {
    try {
        return { samples: sample(), failure: null };
    } catch (error) {
        return {
            samples: [],
            failure: error instanceof Error ? error.name : 'unknown error',
        };
    }
}

/**
 * Samples every dataset that can be compared with the profile: first the
 * image it was drawn on, in the reserved colour, then every loaded image and
 * dose in the same frame of reference, since those need no registration.
 * Datasets in another frame, and datasets that cannot be sampled
 * (SR-WOR-H-17-4), are left out without stopping the comparison, and each is
 * counted so the panel can say so; a sampling failure is logged with the
 * modality only. The results are grouped by display unit, and unitless
 * datasets and relative doses never share an axis.
 *
 * `images` and `doses` are everything loaded for the patient. Deciding what
 * to leave out and counting it happen in one place, so the two cannot
 * disagree.
 */
export function buildProfileComparison(
    profile: LineProfile,
    images: readonly Image3D[],
    doses: readonly DoseMatrix[],
    ownImage: Image3D | undefined
): ProfileComparison {
    const { startMm, endMm } = profile;
    const seeds: DatasetSeed[] = [];

    // An empty FOR UID is unknown: matching by `=== ''` would wrongly
    // co-register every UID-less dataset, so nothing shares a frame with it.
    const forUid = ownImage?.frameOfReferenceUid;
    const sharesFrame = (uid: string): boolean =>
        forUid !== undefined && forUid !== '' && uid === forUid;

    // Dedupe the own image by series UID: the displayed instance may be a
    // different object than the patient's.
    const otherImages = images.filter(
        (image) => image.seriesUid !== ownImage?.seriesUid
    );
    const comparableImages = otherImages.filter((image) =>
        sharesFrame(image.frameOfReferenceUid)
    );
    const comparableDoses = doses.filter((dose) =>
        sharesFrame(dose.dicomDose.frameOfReferenceUid)
    );
    // Without the own image there is no frame to compare against, so the empty
    // comparison is the whole report: there is no frame to call the rest
    // "another" one of.
    const otherFrameOfReference = ownImage
        ? otherImages.length -
          comparableImages.length +
          (doses.length - comparableDoses.length)
        : 0;

    // Own image first, in the reserved colour.
    const orderedImages = ownImage ? [ownImage, ...comparableImages] : [];
    for (const image of orderedImages) {
        const unit = imageIntensityUnit(image);
        const isOwn = image.seriesUid === ownImage?.seriesUid;
        const { samples, failure } = sampleOrOmit(() =>
            sampleImage3DAlongLine(image, startMm, endMm)
        );
        seeds.push({
            key: image.seriesUid,
            label: image.seriesMetadata.seriesDescription,
            modality: image.seriesMetadata.modality,
            unit,
            isOwn,
            samples,
            failure,
            toDisplay: (value) => toDisplayIntensity(value, image),
        });
    }

    for (const dose of comparableDoses) {
        const unit = doseUnitLabel(dose.doseUnits);
        const { samples, failure } = sampleOrOmit(() =>
            sampleDoseAlongLine(dose, startMm, endMm)
        );
        seeds.push({
            key: dose.sopInstanceUid,
            label: dose.dicomDose.seriesDescription,
            modality: 'RTDOSE',
            unit,
            isOwn: false,
            samples,
            failure,
            toDisplay: (value) => value,
        });
    }

    // Colours by seed position, BEFORE the drop, so the reserved own-image
    // colour is never handed to another series when a dataset drops out.
    const datasets: ProfileDataset[] = [];
    let unsampleable = 0;
    for (const [seedIndex, seed] of seeds.entries()) {
        const { failure, ...rest } = seed;
        if (failure !== null) {
            unsampleable++;
            // The modality and the failure kind only: the series description
            // and the UID are PHI and must never reach the console. The
            // sampler refuses a line it cannot profile, so this is the single
            // place a dataset leaves the comparison.
            console.warn(
                `Line profile: omitting a ${rest.modality} dataset (${failure})`
            );
            continue;
        }
        datasets.push({
            ...rest,
            color: datasetColor(seedIndex),
            // The sampler guarantees at least two finite samples or throws,
            // so the accumulator always yields a result.
            stats: summaryStatistics(rest.samples.map((s) => s.value))!,
        });
    }

    const order: string[] = [];
    const byBucket = new Map<string, ProfileDataset[]>();
    for (const dataset of datasets) {
        // Unitless datasets and relative doses are not mutually comparable
        // (arbitrary / per-plan scales), so each buckets on its own key.
        const bucketKey =
            dataset.unit === '' ||
            dataset.unit === doseUnitLabel(DoseUnit.RELATIVE)
                ? `unitless:${dataset.key}`
                : dataset.unit;
        let bucket = byBucket.get(bucketKey);
        if (!bucket) {
            bucket = [];
            byBucket.set(bucketKey, bucket);
            order.push(bucketKey);
        }
        bucket.push(dataset);
    }
    return {
        unitGroups: order.map((bucketKey) => {
            const groupDatasets = byBucket.get(bucketKey)!;
            return { unit: groupDatasets[0].unit, datasets: groupDatasets };
        }),
        omitted: { otherFrameOfReference, unsampleable },
    };
}
